import React, { useState, useEffect } from "react";
import OutfitDetailPanelXLView from "presentational/outfitDetail/OutfitDetailPanelXLView";
import OutfitDetailPanelXSView from "presentational/outfitDetail/OutfitDetailPanelXSView";
import GaHelper from "lib/gaHelper";
import { withStore } from "store/storeHandler";
import { useHistory } from "react-router-dom";
import { getDeviceType } from "lib/deviceInfo";
import { withToken } from "lib/forceTokenRenewComponent";
import { getFullUrl, getEssentialUrl } from "lib/urlQueryParser";
import TXT from "presentational/common/multinational/TXT";
import PropTypes from "prop-types";

OutfitDetailPanel.propTypes = {
  outfitHash: PropTypes.string.isRequired,
  typeOfUser: PropTypes.oneOf(["pending", "owner", "others", "guest"])
    .isRequired,
  onEditModeSelect: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired
};

function OutfitDetailPanel(props) {
  const { outfitHash, typeOfUser, onEditModeSelect, onDeleteClick } = props;
  const {
    isWrongHash,
    infoData,
    itemData,
    calendarData,
    memoData,
    bookmarkData,
    pickData,
    bookmarkFolderList,
    pickFolderList,
    OutfitDetailActions,
    AppErrorActions,
    NoticeModalActions,
    MyOutfitActions,
    BookmarkActions,
    PickActions,
    ItemDetailActions
  } = props;
  const { getIdTokenWT } = props;
  const [hasBookmarkFolderChanged, setHasBookmarkFolderChanged] =
    useState(false);
  const [hasPickFolderChanged, setHasPickFolderChanged] = useState(false);

  const history = useHistory();

  useEffect(() => {
    return () => {
      if (hasBookmarkFolderChanged) {
        updateBookmarkFolderList();
      }
    };
  }, [hasBookmarkFolderChanged]);
  useEffect(() => {
    return () => {
      if (hasPickFolderChanged) {
        updatePickFolderList();
      }
    };
  }, [hasPickFolderChanged]);
  useEffect(() => {
    storeMyOutfitData();
  }, [typeOfUser]);

  const storeMyOutfitData = async () => {
    if (typeOfUser === "pending" || typeOfUser === "guest") return null;

    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;
    try {
      await MyOutfitActions.initList(idToken);
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
    }
  };

  const displayNotice = type => {
    let content;
    switch (type) {
      case "requestBookmarkError":
      case "deleteReplyError":
      case "commonNetworkError":
        content = <TXT name="msg_commonNetworkError" />;
        break;
      case "limitBookmarkError":
        content = <TXT name="msg_bookmarkLimit" />;
        break;
      case "limitBookmarkFolderError":
        content = <TXT name="msg_createFolderLimit" />;
        break;
      case "deletedOutfitError":
        content = <TXT name="msg_outfitNotExist" />;
        break;

      default:
        throw Error(`unknownType => type:"${type}"`);
    }
    NoticeModalActions.openModal({ content });
  };

  const changeBookmark = async () => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;
    try {
      const { needCreative } = await OutfitDetailActions.changeBookmarkData({
        outfitHash,
        idToken
      });
      if (needCreative) {
        displayNotice("limitBookmarkError");
      }
    } catch (error) {
      if (error.message === "DeletedOutfit") {
        displayNotice("deletedOutfitError");
      } else {
        AppErrorActions.setSafeAppError(error);
        displayNotice("requestBookmarkError");
      }
      return null;
    }
  };

  const changeBookmarkFolder = async ({ hash }) => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;
    try {
      await OutfitDetailActions.changeBookmarkFolderData({
        outfitHash,
        folderHash: hash,
        idToken
      });
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
      displayNotice("requestBookmarkError");
      return null;
    }
  };

  const changePick = async () => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;
    try {
      await OutfitDetailActions.changePickData({
        outfitHash,
        idToken
      });
    } catch (error) {
      if (error.message === "DeletedOutfit") {
        displayNotice("deletedOutfitError");
      } else {
        AppErrorActions.setSafeAppError(error);
        displayNotice("requestBookmarkError");
      }
      return null;
    }
  };

  const changePickFolder = async ({ hash }) => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;
    try {
      await OutfitDetailActions.changePickFolderData({
        outfitHash,
        folderHash: hash,
        idToken
      });
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
      displayNotice("requestBookmarkError");
      return null;
    }
  };

  const updateBookmarkFolderList = async () => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;

    BookmarkActions.refreshFolderList({ idToken });
  };

  const updatePickFolderList = async () => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;

    PickActions.refreshFolderList({ idToken });
  };

  const handleBookmarkChange = async ({ hash }) => {
    if (hash === "all") changeBookmark();
    else changeBookmarkFolder({ hash });

    if (hash !== "all" || (hash === "all" && bookmarkData.isBookmarked)) {
      /** 북마크목록의 순서 변동있을 경우 */
      setHasBookmarkFolderChanged(true);
    }

    BookmarkActions.removeOldSavedList({ hash });
  };

  const handlePickChange = async ({ hash }) => {
    if (hash === "all") changePick();
    else changePickFolder({ hash });
    if (hash !== "all" || (hash === "all" && pickData.isPicked)) {
      /** 북마크목록의 순서 변동있을 경우 */
      setHasPickFolderChanged(true);
    }
    PickActions.removeOldSavedList({ hash });
  };

  const handleBookmarkFolderListRequest = async () => {
    updateBookmarkFolderList();
  };

  const handlePickFolderListRequest = async () => {
    updatePickFolderList();
  };

  const handleBookmarkFolderCreateBtnSubmit = async folderName => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;

    if (!folderName || folderName.trim().length === 0) return null;

    try {
      const { needCreative, success } =
        await BookmarkActions.createBookmarkFolder({
          idToken,
          folderName
        });
      if (needCreative) {
        displayNotice("limitBookmarkFolderError");
      } else if (!success) {
        displayNotice("commonNetworkError");
      }
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
    }
  };

  const handlePickFolderCreateBtnSubmit = async folderName => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;

    if (!folderName || folderName.trim().length === 0) return null;

    try {
      const { needCreative, success } = await PickActions.createPickFolder({
        idToken,
        folderName
      });
      if (needCreative) {
        displayNotice("limitBookmarkFolderError");
      } else if (!success) {
        displayNotice("commonNetworkError");
      }
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
    }
  };

  const handleLogInRecommendationButtonClick = ({ type, loc }) => {
    const accountPageUrl = getFullUrl({
      url: getEssentialUrl({ type: "account", allowRefParam: true })(),
      queryObj: {
        next: getEssentialUrl({ type: "outfit" })(outfitHash)
      }
    });

    history.push(accountPageUrl);

    GaHelper.setGAClickEvent({ type, loc });
  };

  const handleOwnersItemClick = itemInfo => {
    ItemDetailActions.initItemInfo(itemInfo);
    history.push(getEssentialUrl({ type: "itemDetail" })(itemInfo.itemHash));
  };

  const handleMemoSubmitClick = async ({ who, where, what, etc }) => {
    try {
      const { idToken, hasTokenExpired } = await getIdTokenWT();
      if (hasTokenExpired) return null;

      OutfitDetailActions.writeOutfitMemoData({
        outfitHash,
        date: calendarData.date,
        memoObj: { who, where, what, etc },
        idToken
      });
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
    }
  };

  const render = () => {
    const deviceType = getDeviceType();

    if (deviceType === "desktop") {
      return (
        <OutfitDetailPanelXLView
          outfitHash={outfitHash}
          onEditModeSelect={onEditModeSelect}
          onDeleteClick={onDeleteClick}
          infoData={infoData}
          itemData={itemData}
          calendarData={calendarData}
          memoData={memoData}
          bookmarkData={bookmarkData}
          pickData={pickData}
          isWrongHash={isWrongHash}
          bookmarkFolderList={bookmarkFolderList}
          pickFolderList={pickFolderList}
          typeOfUser={typeOfUser}
          onBookmarkChange={handleBookmarkChange}
          onPickChange={handlePickChange}
          onBookmarkFolderCreateBtnSubmit={handleBookmarkFolderCreateBtnSubmit}
          onPickFolderCreateBtnSubmit={handlePickFolderCreateBtnSubmit}
          onBookmarkFolderListRequest={handleBookmarkFolderListRequest}
          onPickFolderListRequest={handlePickFolderListRequest}
          onOwnersItemClick={handleOwnersItemClick}
          onMemoSubmitClick={handleMemoSubmitClick}
          onLogInRecommendationButtonClick={
            handleLogInRecommendationButtonClick
          }
        />
      );
    } else if (deviceType === "mobile") {
      return (
        <OutfitDetailPanelXSView
          outfitHash={outfitHash}
          onEditModeSelect={onEditModeSelect}
          onDeleteClick={onDeleteClick}
          infoData={infoData}
          itemData={itemData}
          calendarData={calendarData}
          memoData={memoData}
          bookmarkData={bookmarkData}
          pickData={pickData}
          isWrongHash={isWrongHash}
          bookmarkFolderList={bookmarkFolderList}
          pickFolderList={pickFolderList}
          typeOfUser={typeOfUser}
          onBookmarkChange={handleBookmarkChange}
          onPickChange={handlePickChange}
          onBookmarkFolderCreateBtnSubmit={handleBookmarkFolderCreateBtnSubmit}
          onPickFolderCreateBtnSubmit={handlePickFolderCreateBtnSubmit}
          onBookmarkFolderListRequest={handleBookmarkFolderListRequest}
          onPickFolderListRequest={handlePickFolderListRequest}
          onOwnersItemClick={handleOwnersItemClick}
          onMemoSubmitClick={handleMemoSubmitClick}
          onLogInRecommendationButtonClick={
            handleLogInRecommendationButtonClick
          }
        />
      );
    }
  };

  return render();
}

export default withStore({
  stateMap: {
    outfitDetail: [
      "isWrongHash",
      "infoData",
      "itemData",
      "calendarData",
      "memoData",
      "bookmarkData",
      "pickData"
    ],
    bookmark: ["bookmarkFolderList"],
    pick: ["pickFolderList"]
  },
  dispatchMap: [
    "outfitDetail",
    "appError",
    "noticeModal",
    "myOutfit",
    "bookmark",
    "pick",
    "itemDetail"
  ]
})(withToken(OutfitDetailPanel));
